import { Column, Columns } from '@dixa/lib-shared-habla';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import { CustomForm } from '../../customForms.types';
import { useCustomForms } from '../../hooks/useCustomForms/useCustomForms';

import { BackToFormsListingButton } from './components/backToFormsListing';
import { ActionButtons } from './components/formActionButtons';
import { FormFields } from './components/formFields/formFields';
import { FormPreviewModal } from './components/modals/previewModal';
import { FormTitle } from './components/title';
import { FormValues } from './customFormEditPage.types';
import { useCustomFormEditorModalsState } from './hooks/useFormEditorModals';

function getDefaultValues(form: CustomForm | undefined): FormValues {
  return {
    feedbackAfterSubmit: form?.feedbackAfterSubmit ?? '',
    submitButtonLabel: form?.submitButtonLabel ?? '',
    formFields:
      form?.fields.map((field) => ({
        title: field.title,
        placeholder: field.placeholder,
        id: field.id,
        type: field.type,
        required: field.required,
        options: 'options' in field ? field.options : undefined,
      })) ?? [],
  };
}

function formatFields(values: FormValues): CustomForm['fields'] {
  return values.formFields.map((field) => ({
    title: field.title,
    placeholder: field.placeholder,
    id: field.id,
    type: field.type,
    required: field.required,
    options: (field.options?.length ?? 0) > 0 ? field.options : undefined,
  }));
}

export function CustomFormEditPage(): JSX.Element | null {
  const [currentForm, setCurrentForm] = useState<CustomForm | undefined>(
    undefined,
  );
  const { formPreviewModal } = useCustomFormEditorModalsState();
  const notify = useNotifications();
  const { formId } = useAppPathParams('/settings/custom-forms/edit/:formId');
  const { getSingleForm, updateForm } = useCustomForms();

  const formState = useForm<FormValues>({
    mode: 'all',
    defaultValues: async () => {
      const result = await getSingleForm(formId);
      if (result.ok) {
        setCurrentForm(result.value);
        return getDefaultValues(result.value);
      }
      return getDefaultValues(undefined);
    },
  });

  if (!currentForm) {
    return null;
  }

  const onSubmit = async (values: FormValues) => {
    const result = await updateForm({
      id: formId,
      usage: [], // usage us never updated on client side
      title: currentForm.title,
      fields: formatFields(values),
      submitButtonLabel: values.submitButtonLabel,
      feedbackAfterSubmit: values.feedbackAfterSubmit,
      hasAttributes: values.formFields.length > 0,
    });

    if (result.ok) {
      notify(
        {
          title: 'Form updated',
          time: 5000,
          icon: 'done',
          className: 'success',
        },
        NotificationType.Success,
      );

      return;
    }

    notify(
      {
        title: 'Could not save the form',
        text: result.error,
        icon: 'exclamation',
        className: 'danger',
        time: 5000,
      },
      NotificationType.Error,
    );
  };

  return (
    <FormProvider {...formState}>
      <form onSubmit={formState.handleSubmit(onSubmit)}>
        <Columns>
          <Column>
            <Column.Row slottable>
              <Column.Slot>
                <BackToFormsListingButton />
              </Column.Slot>
            </Column.Row>

            <Column.Row slottable noMinHeight>
              <Column.Slot placement="start">
                <FormTitle form={currentForm} />
              </Column.Slot>

              <Column.Slot placement="end">
                <ActionButtons onPreviewRequest={formPreviewModal.open} />
              </Column.Slot>
            </Column.Row>

            <Column.Row>
              <FormFields />
            </Column.Row>
          </Column>
        </Columns>
      </form>

      <FormPreviewModal
        isOpen={formPreviewModal.isOpen}
        onClose={formPreviewModal.close}
      />
    </FormProvider>
  );
}
